import { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { connect } from 'react-redux'
import {
  Grid,
  MenuItem,
  FormControl,
  Typography,
  Box,
  Slider,
  Tooltip,
  IconButton,
  TextField,
  InputAdornment,
  Switch,
  Autocomplete,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'
import {
  CollapsedField,
  CustomInputLabel,
  CustomSelect,
  CustomTextField,
} from '../restyled-mui/CustomInputs'
import FormHelperText from '@mui/material/FormHelperText'
import DateRangePicker from './DateRangePicker/DateRangePicker'
import HelpIcon from '@mui/icons-material/Help'
import { PrimaryButton } from '../restyled-mui/CustomButton'
import RemoveIcon from '@mui/icons-material/Remove'
import AddIcon from '@mui/icons-material/Add'
import { setSelectedUserSetup } from '../../redux/actions/userSetups'
import {
  setSelectedSetupPlant,
  setSelectedSetupForecastType,
  setIssueTime,
  setSelectedPercentile,
  setSelectedDateRange,
  setLeadTime,
  setDetailAnalysis,
  setSelectedCompareSetup,
} from '../../redux/actions/metrics'
import ValueLabelComponent from './DateRangePicker/ValueLabelComponent'
import LinearProgress from '@mui/material/LinearProgress'
import Api from '../../api'
import { transformSelectedSetup } from '../../api/utils'
import { loadStateFromLocalStorage } from '../../redux/utilsLocalStorage'
import moment from 'moment'
import { ToggleButtonGroup, ToggleButton } from '@mui/material'
import { getPreviousPath } from '../../redux/reducers'
import { convertLocalIssueTimeToUtc } from '../../utils/time'

const marks = [
  { value: 0, label: '00:00' },
  { value: 1439, label: '23:59' },
]

const BASE_FORECAST_TYPES = ['ID', 'DAH', 'Composite']

const forecasts = [
  { name: 'per_plant', label: 'Per Plant' },
  { name: 'aggregated', label: 'Aggregated' },
]

// an aggregation value arrives as { [name]: {...} }, and the name is its value
const getAggregationValueKey = aggregationValue =>
  Object.keys(aggregationValue)[0]

const sitesListWidth = 350
const tabletSidebarWidth = 50

const useStyles = makeStyles(theme => ({
  childrenContainer: {
    marginLeft: props =>
      props.fullscreen || props.toggleable ? 0 : sitesListWidth,
    width: props =>
      props.fullscreen || props.toggleable
        ? '100%'
        : `calc(100% - ${sitesListWidth}px)`,
    display: 'flex',
    flexDirection: 'column',
  },
  formContainer: {
    backgroundColor: theme.palette.common.white,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: '15px 25px',
  },
  drawer: {
    marginLeft: props => (props.toggleable ? tabletSidebarWidth - 1 : 0),
    borderRight: `${theme.palette.menu.border} 1px solid`,
    width: props => (props.fullscreen ? '100%' : sitesListWidth),
  },
  menu: {
    display: 'flex',
    flexDirection: 'column',
    width: tabletSidebarWidth,
    zIndex: 1400,
    backgroundColor: theme.palette.menu.bg,
    borderRight: `${theme.palette.menu.border} 1px solid`,
  },
  menuItem: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2, 4),
  },
  menuItemIcon: {
    textAlign: 'center',
    minWidth: '100%',
  },
  menuIcon: {
    color: theme.palette.menu.icons,
  },
  selectedMenuIcon: {
    color: theme.palette.primary.main,
  },
  tickIcon: {
    color: theme.palette.primary.main,
    marginRight: theme.spacing(2),
  },
  customSubtitle: {
    fontSize: 14,
    color: theme.palette.primary.main,
    marginBottom: '10px',
  },
  timeSlider: {
    marginTop: '33px',
    transform: 'translateZ(0)', // Force GPU acceleration
    willChange: 'transform', // Optimize for transforms
  },
  calendarField: {
    paddingRight: 0,
  },
  checkboxText: {
    color: theme.palette.primary.main,
    marginTop: '12px',
  },
  tooltip: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    fontSize: theme.typography.fontSize,
  },
  arrow: {
    color: theme.palette.primary.main,
  },
  helpIcon: {
    color: theme.palette.primary.main,
  },
  customText: {
    fontSize: 14,
    color: theme.palette.primary.main,
    padding: '30px 0px',
  },
  startMetricButton: {
    width: '100%',
  },
  tooltipContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: '4px',
  },
  sectionDelimiter: {
    paddingTop: '20px',
  },
  runButtonDelimiter: {
    padding: '40px 0px',
  },
  plantsDateErrorCss: {
    margin: '0px 5px',
    color: '#C63C3C',
  },
  toggleButtonPercent: {
    '&.Mui-selected': {
      backgroundColor: '#E6F1FC',
      color: '#368ED8',
      borderColor: '#A9CFF2',
    },

    '&.Mui-selected:hover': {
      backgroundColor: '#D6E9FB',
    },

    '&.Mui-selected.Mui-disabled': {
      backgroundColor: '#eee',
      color: '#888 !important',
      borderColor: '#ddd !important',
      opacity: 1,
      pointerEvents: 'none',
    },
  },
}))

const toggleButtonGroupStyles = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',

  '& .MuiToggleButtonGroup-grouped': {
    margin: '4px !important',
    border: '1px solid rgba(0,0,0,0.12) !important',
  },

  '& .MuiToggleButton-root': {
    flex: '1 0 calc(100% / 7 - 8px)',
    maxWidth: 'calc(100% / 7 - 8px)',
  },
}

const PerformanceCompareForm = props => {
  const classes = useStyles(props)
  const {
    selectedSetup: rawSelectedSetup,
    selectedCompareSetup: rawSelectedCompareSetup,
    setSelectedUserSetup,
    setSelectedCompareSetup,
    userSetups,
    userPowerSetups,
    setMetricsParameters,
    handleNoDataContentUpdate,
    setMetricsData,
    disableButton,
    selectedSetupPlant,
    setSelectedSetupPlant,
    selectedSetupForecastType,
    setSelectedSetupForecastType,
    issueTime,
    setIssueTime,
    selectedPercentile,
    setSelectedPercentile,
    selectedDateRange,
    setSelectedDateRange,
    leadTime,
    setLeadTime,
    detailAnalysis,
    setDetailAnalysis,
    trackDisableStatus,
  } = props

  // Validate selectedSetup and selectedCompareSetup against userPowerSetups
  const selectedSetup = userPowerSetups.includes(rawSelectedSetup)
    ? rawSelectedSetup
    : null
  const selectedCompareSetup = userPowerSetups.includes(rawSelectedCompareSetup)
    ? rawSelectedCompareSetup
    : null

  const location = useLocation()

  const [timeDisableBtn, setTimeDisableBtn] = useState(false)

  /* What each fetched list belongs to. Until it matches the current selection
     the list is the previous one's, and must not be auto-selected. Values are
     fetched per level, so they are keyed by setup and level together. */
  const plantsSetup = useRef(null)
  const plantsS2Setup = useRef(null)
  const levelsS1Setup = useRef(null)
  const levelsS2Setup = useRef(null)
  const valuesS1Key = useRef(null)
  const valuesS2Key = useRef(null)

  // plants (S1)
  const [plants, setPlants] = useState(null)
  const [plantLoading, setPlantLoading] = useState(false)
  const [plantsError, setPlantsError] = useState('')

  // plants (S2)
  const [plantsS2, setPlantsS2] = useState(null)
  const [plantS2Loading, setPlantS2Loading] = useState(false)
  const [plantsS2Error, setPlantsS2Error] = useState('')
  const [selectedPlantS2, setSelectedPlantS2] = useState(null)

  // setup resolutions
  const [resolutionS1, setResolutionS1] = useState(null)
  const [resolutionS2, setResolutionS2] = useState(null)

  // percentiles
  const [percentiles, setPercentiles] = useState([])
  const [percentilesLoading, setPercentilesLoading] = useState(false)
  const [percentValid, setPercentValid] = useState(true)

  // unified plant selection
  const [hasMatchingPlants, setHasMatchingPlants] = useState(false)
  const [useUnifiedPlantSelection, setUseUnifiedPlantSelection] =
    useState(false)

  // unified aggregation selection
  const [hasMatchingAggregations, setHasMatchingAggregations] = useState(false)
  const [useUnifiedAggregationSelection, setUseUnifiedAggregationSelection] =
    useState(false)

  // forecast selection
  const [selectedSetupForecast, setSelectedSetupForecast] = useState({
    name: 'per_plant',
    label: 'Per Plant',
  })

  // aggregation levels and values (S1)
  const [aggregationLevelsS1, setAggregationLevelsS1] = useState([])
  const [aggregationLevelsS1Loading, setAggregationLevelsS1Loading] =
    useState(false)
  const [selectedAggregationLevelS1, setSelectedAggregationLevelS1] =
    useState(null)
  const [aggregationValuesS1, setAggregationValuesS1] = useState([])
  const [aggregationValuesS1Loading, setAggregationValuesS1Loading] =
    useState(false)
  const [selectedAggregationValueS1, setSelectedAggregationValueS1] =
    useState('')

  // aggregation levels and values (S2)
  const [aggregationLevelsS2, setAggregationLevelsS2] = useState([])
  const [aggregationLevelsS2Loading, setAggregationLevelsS2Loading] =
    useState(false)
  const [selectedAggregationLevelS2, setSelectedAggregationLevelS2] =
    useState(null)
  const [aggregationValuesS2, setAggregationValuesS2] = useState([])
  const [aggregationValuesS2Loading, setAggregationValuesS2Loading] =
    useState(false)
  const [selectedAggregationValueS2, setSelectedAggregationValueS2] =
    useState('')

  // const [formState, setFormState] = useState({
  //   percentError: '',
  // })

  const [storedDataReady, setStoredDataReady] = useState(false)

  // delete this commented out after some time
  // const [forecastTypes, setForecastTypes] = useState(BASE_FORECAST_TYPES)
  const isIssueTime = ['ID', 'DAH'].includes(selectedSetupForecastType)
  const isComposite = selectedSetupForecastType === 'Composite'
  // const isLatestForecast = selectedSetupForecastType === 'Latest'

  useEffect(() => {
    /* 
    Resets the selected plant when coming from any route except /latest,
    so the plant selection is preserved when returning from the Latest page.
    */
    const prevPath = getPreviousPath()
    if (prevPath !== '/latest') {
      setSelectedSetupPlant(null)
    }
    setStoredDataReady(true)
  }, [location.search])

  const timezone =
    loadStateFromLocalStorage('timezone') !== undefined
      ? loadStateFromLocalStorage('timezone')
      : 'Etc/UTC'

  const issueStartUtc = convertLocalIssueTimeToUtc(
    issueTime,
    selectedDateRange?.startDate ?? new Date(),
    timezone,
  )
  const issueEndUtc = convertLocalIssueTimeToUtc(
    issueTime,
    selectedDateRange?.endDate ?? selectedDateRange?.startDate ?? new Date(),
    timezone,
  )
  const issueTimeUTC = issueStartUtc.minutes

  const handleChangeLeadTime = e => {
    setMetricsData(null)
    const inputValue = parseInt(e.target.value, 10)

    if (!isNaN(inputValue)) {
      const nonNegativeValue = Math.max(0, inputValue)

      setLeadTime(nonNegativeValue)
    } else {
      setLeadTime(0)
    }
  }

  const handleSetupListData = (_, newValue) => {
    setSelectedSetupPlant(null)
    setSelectedPlantS2(null)
    setSelectedPercentile([50]) //add this to always on setup name change reset to 50
    setUseUnifiedPlantSelection(false) // Reset unified selection when setup changes
    setUseUnifiedAggregationSelection(false) // Reset unified aggregation selection
    // Reset aggregation state for S1
    setSelectedAggregationLevelS1(null)
    setSelectedAggregationValueS1('')
    setSelectedUserSetup(newValue)
    if (newValue === null) {
      setSelectedCompareSetup(null)
      setMetricsData(null)
      handleNoDataContentUpdate({
        heading: 'No setup selected',
        body: 'Select a setup and adjust parameters in order to start analysis',
      })
    } else {
      handleNoDataContentUpdate({ heading: '', body: '' })
    }
  }

  const handleCompareSetupChange = (_, newValue) => {
    setSelectedCompareSetup(newValue)
    setSelectedPlantS2(null)
    setUseUnifiedPlantSelection(false) // Reset unified selection when compare setup changes
    setUseUnifiedAggregationSelection(false) // Reset unified aggregation selection
    // Reset aggregation state for S2
    setSelectedAggregationLevelS2(null)
    setSelectedAggregationValueS2('')
    setMetricsData(null)
  }

  // get S1 setup resolution
  useEffect(() => {
    const fetchResolutionS1 = async () => {
      try {
        const setupName = transformSelectedSetup(selectedSetup)
        const response = await Api.Setups.getSetup(setupName)
        setResolutionS1(response.data?.setup.config?.resolution_minutes ?? null)
      } catch (error) {
        console.error('Error fetching S1 setup resolution:', error)
        setResolutionS1(null)
      }
    }
    if (selectedSetup !== null) {
      fetchResolutionS1()
    } else {
      setResolutionS1(null)
    }
  }, [selectedSetup])

  // get S2 setup resolution
  useEffect(() => {
    const fetchResolutionS2 = async () => {
      try {
        const setupName = transformSelectedSetup(selectedCompareSetup)
        const response = await Api.Setups.getSetup(setupName)
        setResolutionS2(response.data?.setup.config?.resolution_minutes ?? null)
      } catch (error) {
        console.error('Error fetching S2 setup resolution:', error)
        setResolutionS2(null)
      }
    }
    if (selectedCompareSetup !== null) {
      fetchResolutionS2()
    } else {
      setResolutionS2(null)
    }
  }, [selectedCompareSetup])

  const resolutionMismatch =
    selectedCompareSetup !== null &&
    resolutionS1 !== null &&
    resolutionS2 !== null &&
    resolutionS1 !== resolutionS2

  // Check if plants match between S1 and S2
  const checkMatchingPlants = useCallback(() => {
    if (!plants || !plantsS2) {
      setHasMatchingPlants(false)
      setUseUnifiedPlantSelection(false)
      return
    }

    // Compare plant arrays - check if they have any plants in common
    const plantsS1Set = new Set(plants)
    const plantsS2Set = new Set(plantsS2)

    // Check for intersection (any plants in common)
    const hasIntersection = [...plantsS1Set].some(plant =>
      plantsS2Set.has(plant),
    )

    setHasMatchingPlants(hasIntersection)
    if (!hasIntersection) {
      setUseUnifiedPlantSelection(false)
    }
  }, [plants, plantsS2])

  // Get common plants between S1 and S2
  const getCommonPlants = () => {
    if (!plants || !plantsS2) return []
    const plantsS2Set = new Set(plantsS2)
    return plants.filter(plant => plantsS2Set.has(plant))
  }

  // Check if aggregation levels match between S1 and S2
  const checkMatchingAggregations = useCallback(() => {
    if (
      !aggregationLevelsS1 ||
      !aggregationLevelsS2 ||
      aggregationLevelsS1.length === 0 ||
      aggregationLevelsS2.length === 0
    ) {
      setHasMatchingAggregations(false)
      setUseUnifiedAggregationSelection(false)
      return
    }

    const levelsS1Set = new Set(aggregationLevelsS1)
    const levelsS2Set = new Set(aggregationLevelsS2)

    const hasIntersection = [...levelsS1Set].some(level =>
      levelsS2Set.has(level),
    )

    setHasMatchingAggregations(hasIntersection)
    if (!hasIntersection) {
      setUseUnifiedAggregationSelection(false)
    }
  }, [aggregationLevelsS1, aggregationLevelsS2])

  // Get common aggregation levels between S1 and S2
  const getCommonAggregationLevels = () => {
    if (!aggregationLevelsS1 || !aggregationLevelsS2) return []
    const levelsS2Set = new Set(aggregationLevelsS2)
    return aggregationLevelsS1.filter(level => levelsS2Set.has(level))
  }
  // get setup plants
  useEffect(() => {
    const fetchPlants = async () => {
      setPlantLoading(true)
      try {
        // const response = await axios.get('/metrics.json')
        const response = await Api.Metrics.getSetupPlants(
          selectedSetup,
          selectedDateRange,
        )

        const startDate = moment(selectedDateRange.startDate).format(
          'YYYY-MM-DD',
        )
        const endDate = moment(selectedDateRange.endDate).format('YYYY-MM-DD')

        // until get proper response
        if (response.status === 200) {
          plantsSetup.current = selectedSetup
          if (response.data.length !== 0) {
            setPlants(response.data)
            handleNoDataContentUpdate({ heading: '', body: '' })
            // setPlantsDateError('')
            setPlantsError('')
            // setDsbBtn(false)
            // setInitialPlantsLoad(false)
          } else {
            handleNoDataContentUpdate({
              heading: 'Info:',
              body: 'Please change the DATE SELECTION to get the active plants.',
            })
            setPlantsError(
              `No plants for selected period: ${startDate} to ${endDate}.`,
            )
            setPlants(null)
          }
        }
        setPlantLoading(false)
      } catch (error) {
        setPlants(null)
        setPlantsError('Error fetching plants for selected setup')
        console.error('Error fetching plants for selected setup:', error)
      } finally {
        setPlantLoading(false)
      }
    }
    if (selectedSetup !== null) {
      fetchPlants()
    } else {
      setPlants(null)
    }
  }, [selectedSetup, selectedDateRange])

  // get S2 setup plants
  useEffect(() => {
    const fetchPlantsS2 = async () => {
      setPlantS2Loading(true)
      try {
        const response = await Api.Metrics.getSetupPlants(
          selectedCompareSetup,
          selectedDateRange,
        )

        if (response.status === 200) {
          plantsS2Setup.current = selectedCompareSetup
          if (response.data.length !== 0) {
            setPlantsS2(response.data)
            setPlantsS2Error('')
          } else {
            setPlantsS2Error('No plants for selected period in S2 setup.')
            setPlantsS2(null)
          }
        }
        setPlantS2Loading(false)
      } catch (error) {
        setPlantsS2(null)
        setPlantsS2Error('Error fetching plants for S2 setup')
        console.error('Error fetching plants for S2 setup:', error)
      } finally {
        setPlantS2Loading(false)
      }
    }
    if (selectedCompareSetup !== null && selectedDateRange) {
      fetchPlantsS2()
    } else {
      setPlantsS2(null)
      setSelectedPlantS2(null)
    }
  }, [selectedCompareSetup, selectedDateRange])

  // Check for matching plants when both plants arrays are updated
  useEffect(() => {
    checkMatchingPlants()
  }, [checkMatchingPlants])

  // Check for matching aggregations when both aggregation level arrays are updated
  useEffect(() => {
    checkMatchingAggregations()
  }, [checkMatchingAggregations])

  // get S1 setup aggregation levels
  useEffect(() => {
    const fetchAggregationLevelS1 = async () => {
      setAggregationLevelsS1Loading(true)
      try {
        const response =
          await Api.Metrics.getClientAggregationLevels(selectedSetup)
        setAggregationLevelsS1(response.data)
        levelsS1Setup.current = selectedSetup
        setAggregationLevelsS1Loading(false)
      } catch (error) {
        setAggregationLevelsS1([])
        levelsS1Setup.current = selectedSetup
        console.error(
          'Error fetching S1 aggregation levels for selected setup:',
          error,
        )
      } finally {
        setAggregationLevelsS1Loading(false)
      }
    }
    if (selectedSetup !== null && selectedSetupForecast.name === 'aggregated') {
      fetchAggregationLevelS1()
    } else {
      setAggregationLevelsS1([])
    }
  }, [selectedSetup, selectedSetupForecast.name])

  // get S2 setup aggregation levels
  useEffect(() => {
    const fetchAggregationLevelS2 = async () => {
      setAggregationLevelsS2Loading(true)
      try {
        const response =
          await Api.Metrics.getClientAggregationLevels(selectedCompareSetup)
        setAggregationLevelsS2(response.data)
        levelsS2Setup.current = selectedCompareSetup
        setAggregationLevelsS2Loading(false)
      } catch (error) {
        setAggregationLevelsS2([])
        levelsS2Setup.current = selectedCompareSetup
        console.error(
          'Error fetching S2 aggregation levels for selected setup:',
          error,
        )
      } finally {
        setAggregationLevelsS2Loading(false)
      }
    }
    if (
      selectedCompareSetup !== null &&
      selectedSetupForecast.name === 'aggregated'
    ) {
      fetchAggregationLevelS2()
    } else {
      setAggregationLevelsS2([])
    }
  }, [selectedCompareSetup, selectedSetupForecast.name])

  // get S1 setup aggregation values
  useEffect(() => {
    const fetchAggregationValuesS1 = async () => {
      setAggregationValuesS1Loading(true)
      try {
        const response = await Api.Metrics.getClientAggregationValues(
          selectedSetup,
          selectedAggregationLevelS1,
        )
        setAggregationValuesS1(response.data)
        valuesS1Key.current = `${selectedSetup}|${selectedAggregationLevelS1}`
        setAggregationValuesS1Loading(false)
      } catch (error) {
        setAggregationValuesS1([])
        valuesS1Key.current = `${selectedSetup}|${selectedAggregationLevelS1}`
        console.error(
          'Error fetching S1 aggregation values for selected setup:',
          error,
        )
      } finally {
        setAggregationValuesS1Loading(false)
      }
    }
    if (selectedSetup !== null && selectedAggregationLevelS1 !== null) {
      fetchAggregationValuesS1()
    } else {
      setAggregationValuesS1([])
    }
  }, [selectedSetup, selectedAggregationLevelS1])

  // get S2 setup aggregation values
  useEffect(() => {
    const fetchAggregationValuesS2 = async () => {
      setAggregationValuesS2Loading(true)
      try {
        const response = await Api.Metrics.getClientAggregationValues(
          selectedCompareSetup,
          selectedAggregationLevelS2,
        )
        setAggregationValuesS2(response.data)
        valuesS2Key.current = `${selectedCompareSetup}|${selectedAggregationLevelS2}`
        setAggregationValuesS2Loading(false)
      } catch (error) {
        setAggregationValuesS2([])
        valuesS2Key.current = `${selectedCompareSetup}|${selectedAggregationLevelS2}`
        console.error(
          'Error fetching S2 aggregation values for selected setup:',
          error,
        )
      } finally {
        setAggregationValuesS2Loading(false)
      }
    }
    if (selectedCompareSetup !== null && selectedAggregationLevelS2 !== null) {
      fetchAggregationValuesS2()
    } else {
      setAggregationValuesS2([])
    }
  }, [selectedCompareSetup, selectedAggregationLevelS2])

  /* When there is only one option the select is collapsed to plain text, so
     nothing would ever select it: do it here. Each list must belong to the
     current selection, otherwise on a setup change we would select an option
     from the previous setup before its own list has arrived.

     In unified mode the plant and level are picked from the S1/S2 intersection
     and both sides are set together, mirroring what the shared select does. */

  // with a single setup S2 can only be that same setup (S1 is set by the page)
  useEffect(() => {
    if (userSetups.length === 1 && !selectedCompareSetup) {
      setSelectedCompareSetup(userSetups[0])
    }
  }, [userSetups, selectedCompareSetup, setSelectedCompareSetup])

  useEffect(() => {
    if (useUnifiedPlantSelection) return
    if (
      plantsSetup.current === selectedSetup &&
      plants?.length === 1 &&
      !selectedSetupPlant
    ) {
      setSelectedSetupPlant(plants[0])
    }
  }, [
    useUnifiedPlantSelection,
    selectedSetup,
    plants,
    selectedSetupPlant,
    setSelectedSetupPlant,
  ])

  useEffect(() => {
    if (useUnifiedPlantSelection) return
    if (
      plantsS2Setup.current === selectedCompareSetup &&
      plantsS2?.length === 1 &&
      !selectedPlantS2
    ) {
      setSelectedPlantS2(plantsS2[0])
    }
  }, [
    useUnifiedPlantSelection,
    selectedCompareSetup,
    plantsS2,
    selectedPlantS2,
  ])

  useEffect(() => {
    if (!useUnifiedPlantSelection) return
    if (
      plantsSetup.current !== selectedSetup ||
      plantsS2Setup.current !== selectedCompareSetup ||
      !plants ||
      !plantsS2
    ) {
      return
    }

    const plantsS2Set = new Set(plantsS2)
    const commonPlants = plants.filter(plant => plantsS2Set.has(plant))

    if (commonPlants.length === 1 && !selectedSetupPlant) {
      setSelectedSetupPlant(commonPlants[0])
      setSelectedPlantS2(commonPlants[0])
    }
  }, [
    useUnifiedPlantSelection,
    selectedSetup,
    selectedCompareSetup,
    plants,
    plantsS2,
    selectedSetupPlant,
    setSelectedSetupPlant,
  ])

  useEffect(() => {
    if (useUnifiedAggregationSelection) return
    if (
      levelsS1Setup.current === selectedSetup &&
      aggregationLevelsS1.length === 1 &&
      !selectedAggregationLevelS1
    ) {
      setSelectedAggregationLevelS1(aggregationLevelsS1[0])
    }
  }, [
    useUnifiedAggregationSelection,
    selectedSetup,
    aggregationLevelsS1,
    selectedAggregationLevelS1,
    setSelectedAggregationLevelS1,
  ])

  useEffect(() => {
    if (useUnifiedAggregationSelection) return
    if (
      levelsS2Setup.current === selectedCompareSetup &&
      aggregationLevelsS2.length === 1 &&
      !selectedAggregationLevelS2
    ) {
      setSelectedAggregationLevelS2(aggregationLevelsS2[0])
    }
  }, [
    useUnifiedAggregationSelection,
    selectedCompareSetup,
    aggregationLevelsS2,
    selectedAggregationLevelS2,
    setSelectedAggregationLevelS2,
  ])

  useEffect(() => {
    if (!useUnifiedAggregationSelection) return
    if (
      levelsS1Setup.current !== selectedSetup ||
      levelsS2Setup.current !== selectedCompareSetup
    ) {
      return
    }

    const levelsS2Set = new Set(aggregationLevelsS2)
    const commonLevels = aggregationLevelsS1.filter(level =>
      levelsS2Set.has(level),
    )

    if (commonLevels.length === 1 && !selectedAggregationLevelS1) {
      setSelectedAggregationLevelS1(commonLevels[0])
      setSelectedAggregationLevelS2(commonLevels[0])
    }
  }, [
    useUnifiedAggregationSelection,
    selectedSetup,
    selectedCompareSetup,
    aggregationLevelsS1,
    aggregationLevelsS2,
    selectedAggregationLevelS1,
    setSelectedAggregationLevelS1,
    setSelectedAggregationLevelS2,
  ])

  useEffect(() => {
    const belongsToSelection =
      valuesS1Key.current === `${selectedSetup}|${selectedAggregationLevelS1}`

    if (
      belongsToSelection &&
      aggregationValuesS1.length === 1 &&
      selectedAggregationValueS1 === ''
    ) {
      const onlyValue = getAggregationValueKey(aggregationValuesS1[0])
      setSelectedAggregationValueS1(onlyValue)
      // the shared select sets both sides, so mirror it here too
      if (useUnifiedAggregationSelection) {
        setSelectedAggregationValueS2(onlyValue)
      }
    }
  }, [
    useUnifiedAggregationSelection,
    selectedSetup,
    selectedAggregationLevelS1,
    aggregationValuesS1,
    selectedAggregationValueS1,
    setSelectedAggregationValueS1,
    setSelectedAggregationValueS2,
  ])

  useEffect(() => {
    if (useUnifiedAggregationSelection) return

    const belongsToSelection =
      valuesS2Key.current ===
      `${selectedCompareSetup}|${selectedAggregationLevelS2}`

    if (
      belongsToSelection &&
      aggregationValuesS2.length === 1 &&
      selectedAggregationValueS2 === ''
    ) {
      setSelectedAggregationValueS2(
        getAggregationValueKey(aggregationValuesS2[0]),
      )
    }
  }, [
    useUnifiedAggregationSelection,
    selectedCompareSetup,
    selectedAggregationLevelS2,
    aggregationValuesS2,
    selectedAggregationValueS2,
    setSelectedAggregationValueS2,
  ])

  // get setup percentiles
  useEffect(() => {
    const fetchPercentiles = async () => {
      setPercentilesLoading(true)
      try {
        const response = await Api.Metrics.getSetupPercentiles(
          selectedSetup,
          selectedSetupForecast,
        )
        const percentiles = response.data
        const percentilesAsc = percentiles.sort((a, b) => a - b)
        setPercentiles(percentilesAsc)
        setPercentilesLoading(false)
      } catch (error) {
        setPercentiles([])
        console.error('Error fetching percentiles for selected setup:', error)
      } finally {
        setPercentilesLoading(false)
      }
    }
    if (selectedSetup !== null) {
      fetchPercentiles()
    } else {
      setPercentiles([])
    }
  }, [selectedSetup, selectedSetupForecast])

  const createMetricsObject = (setupOverride, plantOverride, isS2 = false) => {
    const setupToUse = setupOverride || selectedSetup
    const initObj = {
      client: transformSelectedSetup(setupToUse),
      forecast: selectedSetupForecast.name,
      forecast_type: selectedSetupForecastType,
      percentiles: selectedPercentile,
      details: detailAnalysis,
    }

    const isPerPlant = selectedSetupForecast.name === 'per_plant'
    const isComposite = selectedSetupForecastType === 'Composite'
    const isLatest = selectedSetupForecastType === 'Latest'
    const usesUtcIssueDates = ['ID', 'DAH'].includes(selectedSetupForecastType)

    const fullInitObj = isLatest
      ? initObj
      : {
          ...initObj,
          start_date: usesUtcIssueDates
            ? issueStartUtc.date
            : moment(selectedDateRange.startDate).format('YYYY-MM-DD'),
          end_date: usesUtcIssueDates
            ? issueEndUtc.date
            : moment(selectedDateRange.endDate).format('YYYY-MM-DD'),
        }

    if (isPerPlant) {
      const base = {
        ...fullInitObj,
        plant: plantOverride !== undefined ? plantOverride : selectedSetupPlant,
      }

      if (isComposite) {
        return {
          ...base,
          lead_time: leadTime,
          issue_time: 600,
        }
      }

      if (isLatest) {
        return { ...base }
      }

      // ID/DAH
      return {
        ...base,
        issue_time: issueTimeUTC,
      }
    }

    // aggregated branch
    const aggregationValues = isS2 ? aggregationValuesS2 : aggregationValuesS1
    const selectedAggregationValue = isS2
      ? selectedAggregationValueS2
      : selectedAggregationValueS1
    const selectedAggregationLevel = isS2
      ? selectedAggregationLevelS2
      : selectedAggregationLevelS1

    const aggregationEntry = aggregationValues.find(
      item => selectedAggregationValue in item,
    )

    const commonProps = {
      ...fullInitObj,
      aggregation_level: selectedAggregationLevel,
      aggregation_value: aggregationEntry
        ? aggregationEntry[selectedAggregationValue]
        : undefined,
    }

    if (isComposite) {
      // aggregated + Composite
      return {
        ...commonProps,
        lead_time: leadTime,
      }
    }

    if (isLatest) {
      // aggregated + Latest
      return {
        ...commonProps,
      }
    }

    // aggregated + ID/DAH
    return {
      ...commonProps,
      issue_time: issueTimeUTC,
    }
  }

  const startMetrics = () => {
    const forecastObj = createMetricsObject(
      selectedSetup,
      selectedSetupPlant,
      false,
    )
    const backtestObj = selectedCompareSetup
      ? createMetricsObject(selectedCompareSetup, selectedPlantS2, true)
      : null

    setMetricsParameters({
      forecast: forecastObj,
      backtest: backtestObj,
      comparisonSelection: {
        setupS1: selectedSetup,
        setupS2: selectedCompareSetup,
        plantS1: forecastObj.plant,
        plantS2: backtestObj?.plant,
        aggregationS1: selectedAggregationValueS1,
        aggregationS2: selectedAggregationValueS2,
        forecastType: selectedSetupForecastType,
        issueTime,
        leadTime,
      },
    })
  }

  const isEmptyOrContainsUndefined =
    userSetups.length === 0 || userSetups.some(x => x === undefined)

  // Only one option to pick from, so there is nothing to pick: show as text.
  const isSingleSetup = userSetups.length === 1

  const renderSetupName = () => {
    if (isSingleSetup) {
      return (
        <Grid item md={12}>
          <CollapsedField
            label={
              <>
                Setup (<span style={{ color: '#368ED8' }}>S1</span>)
              </>
            }
            value={userSetups[0]}
          />
          <CollapsedField
            label={
              <>
                Setup (<span style={{ color: '#F5A623' }}>S2</span>) to compare
                to
              </>
            }
            value={userSetups[0]}
          />
          {resolutionMismatch && (
            <FormHelperText error>
              Resolution mismatch: S1 ({resolutionS1} min) ≠ S2 ({resolutionS2}{' '}
              min). Setups must share the same resolution.
            </FormHelperText>
          )}
        </Grid>
      )
    }

    return (
      <Grid item md={12}>
        {/* S1 — Forecast setup */}
        <div className={classes.tooltipContainer}>
          <CustomInputLabel>
            Setup (<span style={{ color: '#368ED8' }}>S1</span>)
          </CustomInputLabel>
          <Tooltip
            title="You can search for a value by typing in the box!"
            placement="top"
            arrow
            classes={{
              tooltip: classes.tooltip,
              arrow: classes.arrow,
            }}
          >
            <span>
              <IconButton
                style={{ padding: '0' }}
                aria-label="info"
                disableTouchRipple
              >
                <HelpIcon fontSize="small" className={classes.helpIcon} />
              </IconButton>
            </span>
          </Tooltip>
        </div>
        <FormControl fullWidth variant="outlined" size="small">
          <Autocomplete
            disabled={trackDisableStatus}
            variant="outlined"
            size="small"
            options={userSetups}
            value={selectedSetup}
            autoHighlight
            onChange={handleSetupListData}
            renderInput={params => (
              <CustomTextField
                {...params}
                inputProps={{
                  ...params.inputProps,
                  style: { padding: '1px 5px' },
                }}
              />
            )}
          />
        </FormControl>

        {/* S2 — Backtest setup (compare to) - Only show after S1 is selected */}
        {selectedSetup && (
          <>
            <div
              className={classes.tooltipContainer}
              style={{ marginTop: '12px' }}
            >
              <CustomInputLabel>
                Setup (<span style={{ color: '#F5A623' }}>S2</span>) to compare
                to
              </CustomInputLabel>
            </div>
            <FormControl fullWidth variant="outlined" size="small">
              <Autocomplete
                disabled={trackDisableStatus}
                variant="outlined"
                size="small"
                options={userSetups}
                value={selectedCompareSetup}
                autoHighlight
                onChange={handleCompareSetupChange}
                renderInput={params => (
                  <CustomTextField
                    {...params}
                    inputProps={{
                      ...params.inputProps,
                      style: { padding: '1px 5px' },
                    }}
                  />
                )}
              />
            </FormControl>
            {!selectedCompareSetup && (
              <FormHelperText id="component-error-text">
                Please select a setup (S2) to compare to.
              </FormHelperText>
            )}
            {resolutionMismatch && (
              <FormHelperText error>
                Resolution mismatch: S1 ({resolutionS1} min) ≠ S2 (
                {resolutionS2} min). Setups must share the same resolution.
              </FormHelperText>
            )}
          </>
        )}
      </Grid>
    )
  }

  const renderForecast = () => {
    return (
      <Grid item md={12} className={classes.sectionDelimiter}>
        <Typography
          variant="h4"
          className={clsx(classes.formSubtitle, classes.customSubtitle)}
        >
          Forecast Type Selection
        </Typography>

        <CustomInputLabel>Forecast Type</CustomInputLabel>
        <FormControl
          disabled={trackDisableStatus}
          fullWidth
          margin="dense"
          variant="outlined"
          size="small"
        >
          <CustomSelect
            inputProps={{
              margin: 'dense',
            }}
            value={selectedSetupForecastType}
            onChange={e => {
              setSelectedSetupForecastType(e.target.value)
              setMetricsData(null)
            }}
            name={'forecastTypeId'}
          >
            {BASE_FORECAST_TYPES.map(ftype => {
              return (
                <MenuItem
                  key={ftype}
                  value={ftype}
                  className={classes.selectMenuItem}
                >
                  {ftype}
                </MenuItem>
              )
            })}
          </CustomSelect>
        </FormControl>

        <CustomInputLabel>Forecast</CustomInputLabel>
        <FormControl
          fullWidth
          margin="dense"
          variant="outlined"
          size="small"
          disabled={trackDisableStatus}
        >
          <CustomSelect
            inputProps={{
              margin: 'dense',
            }}
            value={selectedSetupForecast.name}
            onChange={e => {
              const selectedForecastName = e.target.value
              const selectedForecastObject = forecasts.find(
                forecast => forecast.name === selectedForecastName,
              )
              setSelectedSetupForecast(selectedForecastObject)
              // Reset form when forecast type changes
              setSelectedSetupPlant(null)
              setSelectedPlantS2(null)
              setUseUnifiedPlantSelection(false)
              // Reset aggregation state
              setSelectedAggregationLevelS1(null)
              setSelectedAggregationValueS1('')
              setSelectedAggregationLevelS2(null)
              setSelectedAggregationValueS2('')
              setMetricsData(null)
            }}
            name={'forecastId'}
          >
            {forecasts.map(forecast => {
              return (
                <MenuItem
                  key={forecast.name}
                  value={forecast.name}
                  className={classes.selectMenuItem}
                >
                  {forecast.label}
                </MenuItem>
              )
            })}
          </CustomSelect>
        </FormControl>
      </Grid>
    )
  }

  const renderDate = () => {
    return (
      <Grid item md={12} className={classes.sectionDelimiter}>
        <Typography
          variant="h4"
          className={clsx(classes.formSubtitle, classes.customSubtitle)}
        >
          Date selection
        </Typography>
        <Grid container spacing={2} style={{ paddingBottom: '15px' }}>
          <DateRangePicker
            trackDisableStatus={trackDisableStatus}
            initialDateRange={selectedDateRange}
            onDateRangeChange={dateRange => {
              setSelectedDateRange(dateRange)
              setMetricsData(null)
            }}
            onErrorChange={setTimeDisableBtn}
          />
        </Grid>
      </Grid>
    )
  }

  const renderPlants = () => {
    // If unified plant selection is enabled, show only common plants
    if (useUnifiedPlantSelection && hasMatchingPlants) {
      const commonPlants = getCommonPlants()

      const sharedPlantLabel = (
        <>
          Shared Plant (<span style={{ color: '#368ED8' }}>S1</span> &{' '}
          <span style={{ color: '#F5A623' }}>S2</span>)
        </>
      )

      if (!plantLoading && commonPlants.length === 1) {
        return (
          <div>
            <CollapsedField label={sharedPlantLabel} value={commonPlants[0]} />
          </div>
        )
      }

      return (
        <div>
          {plantLoading ? (
            <LinearProgress style={{ marginTop: '20px' }} />
          ) : commonPlants.length > 0 ? (
            <>
              <CustomInputLabel>{sharedPlantLabel}</CustomInputLabel>
              <FormControl fullWidth variant="outlined" size="small">
                <Autocomplete
                  disabled={trackDisableStatus}
                  variant="outlined"
                  size="small"
                  options={commonPlants}
                  value={selectedSetupPlant}
                  autoHighlight
                  onChange={(_, newPlant) => {
                    setSelectedSetupPlant(newPlant)
                    setSelectedPlantS2(newPlant) // Sync both selections
                    setMetricsData(null)
                  }}
                  renderInput={params => (
                    <CustomTextField
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        style: { padding: '1px 5px' },
                      }}
                    />
                  )}
                />
              </FormControl>
            </>
          ) : (
            <CustomInputLabel style={{ color: 'orangered' }}>
              {plantsError || 'No common plants found'}
            </CustomInputLabel>
          )}
        </div>
      )
    }

    // Default behavior: show separate plant selectors for S1 and S2
    return (
      <div>
        {/* ---- Plants (S1) ---- */}
        {plantLoading ? (
          <LinearProgress style={{ marginTop: '20px' }} />
        ) : plants !== null ? (
          plants.length === 1 ? (
            <CollapsedField
              label={
                <>
                  Plant (<span style={{ color: '#368ED8' }}>S1</span>)
                </>
              }
              value={plants[0]}
            />
          ) : (
            <>
              <CustomInputLabel>
                Plant (<span style={{ color: '#368ED8' }}>S1</span>)
              </CustomInputLabel>
              <FormControl fullWidth variant="outlined" size="small">
                <Autocomplete
                  disabled={trackDisableStatus}
                  variant="outlined"
                  size="small"
                  options={plants}
                  value={selectedSetupPlant}
                  autoHighlight
                  onChange={(_, newPlant) => {
                    setSelectedSetupPlant(newPlant)
                    setMetricsData(null)
                  }}
                  renderInput={params => (
                    <CustomTextField
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        style: { padding: '1px 5px' },
                      }}
                    />
                  )}
                />
              </FormControl>
            </>
          )
        ) : (
          <CustomInputLabel style={{ color: 'orangered' }}>
            {plantsError}
          </CustomInputLabel>
        )}

        {/* ---- Plants (S2) ---- */}
        {selectedCompareSetup && (
          <div style={{ marginTop: '12px' }}>
            {plantS2Loading ? (
              <LinearProgress style={{ marginTop: '20px' }} />
            ) : plantsS2 !== null ? (
              plantsS2.length === 1 ? (
                <CollapsedField
                  label={
                    <>
                      Plant (<span style={{ color: '#F5A623' }}>S2</span>)
                    </>
                  }
                  value={plantsS2[0]}
                />
              ) : (
                <>
                  <CustomInputLabel>
                    Plant (<span style={{ color: '#F5A623' }}>S2</span>)
                  </CustomInputLabel>
                  <FormControl fullWidth variant="outlined" size="small">
                    <Autocomplete
                      disabled={trackDisableStatus}
                      variant="outlined"
                      size="small"
                      options={plantsS2}
                      value={selectedPlantS2}
                      autoHighlight
                      onChange={(_, newPlant) => {
                        setSelectedPlantS2(newPlant)
                        setMetricsData(null)
                      }}
                      renderInput={params => (
                        <CustomTextField
                          {...params}
                          inputProps={{
                            ...params.inputProps,
                            style: { padding: '1px 5px' },
                          }}
                        />
                      )}
                    />
                  </FormControl>
                </>
              )
            ) : (
              <CustomInputLabel style={{ color: 'orangered' }}>
                {plantsS2Error}
              </CustomInputLabel>
            )}
          </div>
        )}
      </div>
    )
  }

  const renderIssueTime = () => {
    return (
      <div style={{ marginTop: '10px' }}>
        <CustomInputLabel>Issuetime ({timezone})</CustomInputLabel>
        <Box className={classes.timeSlider}>
          <Slider
            size="small"
            disabled={trackDisableStatus}
            aria-label="Always visible"
            value={issueTime}
            onChange={(_, newSliderValue) => {
              setIssueTime(newSliderValue)
              setMetricsData(null)
            }}
            defaultValue={600}
            step={5}
            marks={marks}
            valueLabelDisplay="on"
            min={0}
            max={1439}
            slots={{ valueLabel: ValueLabelComponent }}
          />
        </Box>
      </div>
    )
  }

  const renderLeadTime = () => {
    return (
      <div>
        <CustomInputLabel>Leadtime (minutes)</CustomInputLabel>
        <TextField
          disabled={trackDisableStatus}
          fullWidth
          margin="dense"
          variant="outlined"
          size="small"
          value={leadTime}
          onChange={handleChangeLeadTime}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="decrease"
                  onClick={() => {
                    const newValue = Math.max(leadTime - 15, 0)
                    setLeadTime(newValue)
                    setMetricsData(null)
                  }}
                  disabled={leadTime - 15 < 0 || trackDisableStatus}
                >
                  <RemoveIcon />
                </IconButton>
                <IconButton
                  disabled={trackDisableStatus}
                  size="small"
                  aria-label="increase"
                  onClick={() => {
                    const newValue = leadTime + 15
                    setLeadTime(newValue)
                    setMetricsData(null)
                  }}
                >
                  <AddIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </div>
    )
  }

  const renderPercentiles = () => {
    return (
      <div>
        <CustomInputLabel style={{ marginBottom: '4px' }}>
          Percentiles
        </CustomInputLabel>
        {percentilesLoading ? (
          <LinearProgress style={{ marginTop: '20px' }} />
        ) : percentiles.length !== 0 ? (
          <>
            <ToggleButtonGroup
              value={selectedPercentile}
              onChange={(event, newSelection) => {
                setSelectedPercentile(newSelection)
                setPercentValid(newSelection.length > 0)
                setMetricsData(null)
              }}
              size="small"
              color="primary"
              disabled={trackDisableStatus}
              fullWidth
              sx={toggleButtonGroupStyles}
            >
              {percentiles.map(percentile => (
                <ToggleButton
                  key={percentile}
                  value={percentile}
                  className={classes.toggleButtonPercent}
                  disabled={trackDisableStatus}
                >
                  {percentile}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {/* <FormHelperText id="component-error-text">
              {formState.percentError}
            </FormHelperText> */}

            {!percentValid && (
              <FormHelperText id="component-error-text">
                At least one selected percentile is required.
              </FormHelperText>
            )}
          </>
        ) : (
          <CustomInputLabel>
            No available percentiles for selected setup.
          </CustomInputLabel>
        )}
      </div>
    )
  }

  const renderRunBtn = () => {
    return (
      <div className={classes.runButtonDelimiter}>
        <PrimaryButton
          onClick={startMetrics}
          style={{
            background:
              (disableButton ||
                timeDisableBtn ||
                !percentValid ||
                !selectedCompareSetup) &&
              '#ccc',
          }}
          className={classes.startMetricButton}
          disabled={
            disableButton ||
            timeDisableBtn ||
            !percentValid ||
            !selectedCompareSetup
          }
        >
          RUN
        </PrimaryButton>
      </div>
    )
  }

  const renderSectionHeadline = caption => {
    return (
      <Typography
        variant="h4"
        className={clsx(classes.formSubtitle, classes.customSubtitle)}
      >
        {caption}
      </Typography>
    )
  }

  const renderDetailAnalysis = () => {
    return (
      <Box display="flex" alignItems="baseline" mt={4}>
        <Switch
          style={{ marginRight: 8 }}
          color="primary"
          size="small"
          checked={detailAnalysis}
          onChange={(_, checked) => {
            setDetailAnalysis(checked)
            setMetricsData(null)
          }}
          disabled={trackDisableStatus}
        />

        <CustomInputLabel>Detailed Analysis</CustomInputLabel>
      </Box>
    )
  }

  const renderAggregationLevelS1 = () => {
    if (!aggregationLevelsS1Loading && aggregationLevelsS1.length === 1) {
      return (
        <div>
          <CollapsedField
            label={
              <>
                Aggregation Level (<span style={{ color: '#368ED8' }}>S1</span>)
              </>
            }
            value={aggregationLevelsS1[0]}
          />
        </div>
      )
    }

    return (
      <div>
        <CustomInputLabel>
          Aggregation Level (<span style={{ color: '#368ED8' }}>S1</span>)
        </CustomInputLabel>
        {aggregationLevelsS1Loading ? (
          <LinearProgress style={{ marginTop: '20px' }} />
        ) : aggregationLevelsS1.length !== 0 ? (
          <FormControl fullWidth variant="outlined" size="small">
            <Autocomplete
              disabled={trackDisableStatus}
              variant="outlined"
              size="small"
              options={aggregationLevelsS1}
              value={selectedAggregationLevelS1}
              autoHighlight
              onChange={(_, newAggrLevel) => {
                setSelectedAggregationLevelS1(newAggrLevel)
                setSelectedAggregationValueS1('')
                setMetricsData(null)
              }}
              renderInput={params => (
                <CustomTextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    style: { padding: '1px 5px' },
                  }}
                />
              )}
            />
          </FormControl>
        ) : (
          <CustomInputLabel>
            No aggregation levels for S1 setup or error occurred.
          </CustomInputLabel>
        )}
      </div>
    )
  }

  const renderAggregationValueS1 = () => {
    if (!aggregationValuesS1Loading && aggregationValuesS1.length === 1) {
      return (
        <div>
          <CollapsedField
            label={
              <>
                Aggregation Value (<span style={{ color: '#368ED8' }}>S1</span>)
              </>
            }
            value={getAggregationValueKey(aggregationValuesS1[0])}
          />
        </div>
      )
    }

    return (
      <div>
        <CustomInputLabel style={{ marginBottom: '3px' }}>
          Aggregation Value (<span style={{ color: '#368ED8' }}>S1</span>)
        </CustomInputLabel>
        {aggregationValuesS1Loading ? (
          <LinearProgress style={{ marginTop: '20px' }} />
        ) : aggregationValuesS1.length !== 0 ? (
          <FormControl
            disabled={trackDisableStatus}
            fullWidth
            variant="outlined"
            size="small"
          >
            <CustomSelect
              inputProps={{
                margin: 'dense',
              }}
              value={selectedAggregationValueS1}
              onChange={e => {
                setSelectedAggregationValueS1(e.target.value)
                setMetricsData(null)
              }}
              name={'aggrValueS1'}
              defaultValue=""
            >
              {aggregationValuesS1.map(obj => {
                const key = getAggregationValueKey(obj)
                return (
                  <MenuItem
                    key={key}
                    value={key}
                    className={classes.selectMenuItem}
                  >
                    {key}
                  </MenuItem>
                )
              })}
            </CustomSelect>
          </FormControl>
        ) : (
          <CustomInputLabel>
            No aggregation values for S1 setup
            <br />
            or error occurred.
          </CustomInputLabel>
        )}
      </div>
    )
  }

  const renderAggregationLevelS2 = () => {
    if (!aggregationLevelsS2Loading && aggregationLevelsS2.length === 1) {
      return (
        <div>
          <CollapsedField
            label={
              <>
                Aggregation Level (<span style={{ color: '#F5A623' }}>S2</span>)
              </>
            }
            value={aggregationLevelsS2[0]}
          />
        </div>
      )
    }

    return (
      <div>
        <CustomInputLabel>
          Aggregation Level (<span style={{ color: '#F5A623' }}>S2</span>)
        </CustomInputLabel>
        {aggregationLevelsS2Loading ? (
          <LinearProgress style={{ marginTop: '20px' }} />
        ) : aggregationLevelsS2.length !== 0 ? (
          <FormControl fullWidth variant="outlined" size="small">
            <Autocomplete
              disabled={trackDisableStatus}
              variant="outlined"
              size="small"
              options={aggregationLevelsS2}
              value={selectedAggregationLevelS2}
              autoHighlight
              onChange={(_, newAggrLevel) => {
                setSelectedAggregationLevelS2(newAggrLevel)
                setSelectedAggregationValueS2('')
                setMetricsData(null)
              }}
              renderInput={params => (
                <CustomTextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    style: { padding: '1px 5px' },
                  }}
                />
              )}
            />
          </FormControl>
        ) : (
          <CustomInputLabel>
            No aggregation levels for S2 setup or error occurred.
          </CustomInputLabel>
        )}
      </div>
    )
  }

  const renderAggregationValueS2 = () => {
    if (!aggregationValuesS2Loading && aggregationValuesS2.length === 1) {
      return (
        <div>
          <CollapsedField
            label={
              <>
                Aggregation Value (<span style={{ color: '#F5A623' }}>S2</span>)
              </>
            }
            value={getAggregationValueKey(aggregationValuesS2[0])}
          />
        </div>
      )
    }

    return (
      <div>
        <CustomInputLabel style={{ marginBottom: '3px' }}>
          Aggregation Value (<span style={{ color: '#F5A623' }}>S2</span>)
        </CustomInputLabel>
        {aggregationValuesS2Loading ? (
          <LinearProgress style={{ marginTop: '20px' }} />
        ) : aggregationValuesS2.length !== 0 ? (
          <FormControl
            disabled={trackDisableStatus}
            fullWidth
            variant="outlined"
            size="small"
          >
            <CustomSelect
              inputProps={{
                margin: 'dense',
              }}
              value={selectedAggregationValueS2}
              onChange={e => {
                setSelectedAggregationValueS2(e.target.value)
                setMetricsData(null)
              }}
              name={'aggrValueS2'}
              defaultValue=""
            >
              {aggregationValuesS2.map(obj => {
                const key = getAggregationValueKey(obj)
                return (
                  <MenuItem
                    key={key}
                    value={key}
                    className={classes.selectMenuItem}
                  >
                    {key}
                  </MenuItem>
                )
              })}
            </CustomSelect>
          </FormControl>
        ) : (
          <CustomInputLabel>
            No aggregation values for S2 setup
            <br />
            or error occurred.
          </CustomInputLabel>
        )}
      </div>
    )
  }

  // Unified aggregation level renderer
  const renderAggregationLevels = () => {
    if (useUnifiedAggregationSelection && hasMatchingAggregations) {
      const commonLevels = getCommonAggregationLevels()
      const levelsLoading =
        aggregationLevelsS1Loading || aggregationLevelsS2Loading

      const sharedLevelLabel = (
        <>
          Shared Aggregation Level (<span style={{ color: '#368ED8' }}>S1</span>{' '}
          & <span style={{ color: '#F5A623' }}>S2</span>)
        </>
      )

      if (!levelsLoading && commonLevels.length === 1) {
        return (
          <div>
            <CollapsedField label={sharedLevelLabel} value={commonLevels[0]} />
          </div>
        )
      }

      return (
        <div>
          <CustomInputLabel>{sharedLevelLabel}</CustomInputLabel>
          {aggregationLevelsS1Loading || aggregationLevelsS2Loading ? (
            <LinearProgress style={{ marginTop: '20px' }} />
          ) : commonLevels.length > 0 ? (
            <FormControl
              fullWidth
              margin="dense"
              variant="outlined"
              size="small"
            >
              <Autocomplete
                disabled={trackDisableStatus}
                variant="outlined"
                size="small"
                options={commonLevels}
                value={selectedAggregationLevelS1}
                autoHighlight
                onChange={(_, newAggrLevel) => {
                  setSelectedAggregationLevelS1(newAggrLevel)
                  setSelectedAggregationLevelS2(newAggrLevel)
                  setSelectedAggregationValueS1('')
                  setSelectedAggregationValueS2('')
                  setMetricsData(null)
                }}
                renderInput={params => (
                  <CustomTextField
                    {...params}
                    inputProps={{
                      ...params.inputProps,
                      style: { padding: '1px 5px' },
                    }}
                  />
                )}
              />
            </FormControl>
          ) : (
            <CustomInputLabel style={{ color: 'orangered' }}>
              No common aggregation levels found
            </CustomInputLabel>
          )}
        </div>
      )
    }

    return (
      <div>
        {renderAggregationLevelS1()}
        {selectedCompareSetup &&
          selectedAggregationLevelS1 &&
          renderAggregationLevelS2()}
      </div>
    )
  }

  // Unified aggregation value renderer
  const renderAggregationValues = () => {
    if (
      useUnifiedAggregationSelection &&
      hasMatchingAggregations &&
      selectedAggregationLevelS1
    ) {
      const sharedValueLabel = (
        <>
          Shared Aggregation Value (<span style={{ color: '#368ED8' }}>S1</span>{' '}
          & <span style={{ color: '#F5A623' }}>S2</span>)
        </>
      )

      if (!aggregationValuesS1Loading && aggregationValuesS1.length === 1) {
        return (
          <div>
            <CollapsedField
              label={sharedValueLabel}
              value={getAggregationValueKey(aggregationValuesS1[0])}
            />
          </div>
        )
      }

      return (
        <div>
          <CustomInputLabel>{sharedValueLabel}</CustomInputLabel>
          {aggregationValuesS1Loading ? (
            <LinearProgress style={{ marginTop: '20px' }} />
          ) : aggregationValuesS1.length !== 0 ? (
            <FormControl
              disabled={trackDisableStatus}
              fullWidth
              margin="dense"
              variant="outlined"
              size="small"
            >
              <CustomSelect
                inputProps={{
                  margin: 'dense',
                }}
                value={selectedAggregationValueS1}
                onChange={e => {
                  setSelectedAggregationValueS1(e.target.value)
                  setSelectedAggregationValueS2(e.target.value)
                  setMetricsData(null)
                }}
                name={'aggrValueUnified'}
                defaultValue=""
              >
                {aggregationValuesS1.map(obj => {
                  const key = getAggregationValueKey(obj)
                  return (
                    <MenuItem
                      key={key}
                      value={key}
                      className={classes.selectMenuItem}
                    >
                      {key}
                    </MenuItem>
                  )
                })}
              </CustomSelect>
            </FormControl>
          ) : (
            <CustomInputLabel>
              No aggregation values for shared level
              <br />
              or error occurred.
            </CustomInputLabel>
          )}
        </div>
      )
    }

    return (
      <div>
        {renderAggregationValueS1()}
        {selectedCompareSetup &&
          selectedAggregationLevelS2 &&
          renderAggregationValueS2()}
      </div>
    )
  }

  return (
    <>
      {storedDataReady && (
        <div className={classes.formContainer}>
          <Grid container spacing={0}>
            <Grid item md={12}>
              <Typography
                variant="h4"
                className={clsx(classes.formSubtitle, classes.customSubtitle)}
                style={{ marginBottom: 0 }}
              >
                Setup selection
              </Typography>
              {!isEmptyOrContainsUndefined ? (
                renderSetupName()
              ) : (
                <CustomInputLabel>
                  No available setup for selection or error occurred.
                </CustomInputLabel>
              )}

              {selectedSetup && !resolutionMismatch && (
                <>
                  {renderForecast()}

                  {renderDate()}

                  {renderSectionHeadline('Configuration')}

                  {/* Per Plant Configuration */}
                  {selectedSetupForecast.name === 'per_plant' && (
                    <>
                      {/* Unified Plant Selection Toggle - only show if plants match */}
                      {hasMatchingPlants && selectedCompareSetup && (
                        <Box display="flex" alignItems="baseline" mt={1} mb={2}>
                          <Switch
                            style={{ marginRight: 8 }}
                            color="primary"
                            size="small"
                            checked={useUnifiedPlantSelection}
                            onChange={(_, checked) => {
                              setUseUnifiedPlantSelection(checked)
                              if (checked) {
                                // When enabling unified selection, sync S2 plant to S1 plant
                                setSelectedPlantS2(selectedSetupPlant)
                              }
                              setMetricsData(null)
                            }}
                            disabled={trackDisableStatus}
                          />
                          <CustomInputLabel>Sync Plants</CustomInputLabel>
                        </Box>
                      )}

                      {renderPlants()}

                      {selectedSetupPlant &&
                        (!selectedCompareSetup || selectedPlantS2) && (
                          <>
                            {isIssueTime && renderIssueTime()}

                            {isComposite && renderLeadTime()}

                            {renderPercentiles()}

                            {renderDetailAnalysis()}

                            {!percentilesLoading &&
                              percentiles.length > 0 &&
                              renderRunBtn()}
                          </>
                        )}
                    </>
                  )}

                  {/* Aggregated Configuration */}
                  {selectedSetupForecast.name === 'aggregated' && (
                    <>
                      {/* Unified Aggregation Selection Toggle - only show if aggregations match */}
                      {hasMatchingAggregations && selectedCompareSetup && (
                        <Box display="flex" alignItems="baseline" mt={1} mb={2}>
                          <Switch
                            style={{ marginRight: 8 }}
                            color="primary"
                            size="small"
                            checked={useUnifiedAggregationSelection}
                            onChange={(_, checked) => {
                              setUseUnifiedAggregationSelection(checked)
                              if (checked) {
                                // When enabling unified selection, sync S2 to S1
                                setSelectedAggregationLevelS2(
                                  selectedAggregationLevelS1,
                                )
                                setSelectedAggregationValueS2(
                                  selectedAggregationValueS1,
                                )
                              }
                              setMetricsData(null)
                            }}
                            disabled={trackDisableStatus}
                          />
                          <CustomInputLabel>Sync Aggregated</CustomInputLabel>
                        </Box>
                      )}

                      {renderAggregationLevels()}

                      {(useUnifiedAggregationSelection
                        ? selectedAggregationLevelS1
                        : selectedAggregationLevelS1 ||
                          selectedAggregationLevelS2) && (
                        <>
                          {renderAggregationValues()}

                          {(useUnifiedAggregationSelection
                            ? selectedAggregationValueS1 !== ''
                            : selectedAggregationValueS1 !== '' &&
                              selectedAggregationValueS2 !== '') && (
                            <>
                              {isIssueTime && renderIssueTime()}

                              {isComposite && renderLeadTime()}

                              {renderPercentiles()}

                              {!percentilesLoading &&
                                percentiles.length > 0 &&
                                renderRunBtn()}
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </div>
      )}
    </>
  )
}

const mapStateToProps = state => {
  return {
    selectedSetup: state.userSetups.selectedSetup,
    selectedCompareSetup: state.metrics.selectedCompareSetup,
    userPowerSetups: state.userSetups.userPowerSetups,
    selectedSetupPlant: state.metrics.selectedPlant,
    selectedSetupForecastType: state.metrics.selectedForecastType,
    issueTime: state.metrics.issueTime,
    selectedPercentile: state.metrics.selectedPercentile,
    selectedDateRange: state.metrics.selectedDateRange,
    leadTime: state.metrics.leadTime,
    detailAnalysis: state.metrics.detailAnalysis,
  }
}

const mapDispatchToProps = {
  setSelectedUserSetup,
  setSelectedCompareSetup,
  setSelectedSetupPlant,
  setSelectedSetupForecastType,
  setIssueTime,
  setSelectedPercentile,
  setSelectedDateRange,
  setLeadTime,
  setDetailAnalysis,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(PerformanceCompareForm)
